// /frontend/src/pages/dashboard/post/previews/PinterestPreview.js
import React, { useState } from 'react'

const VIDEO_EXT   = ['.mp4', '.mov', '.webm', '.avi', '.m4v']
const PLACEHOLDER = 'https://placehold.co/30x30/E60023/FFFFFF?text=P'

const isVideoUrl = (url) =>
  url && VIDEO_EXT.some((ext) => url.toLowerCase().endsWith(ext))

export default function PinterestPreview({
  account,
  files,
  caption,
  postType,
  scheduleDateTime,
}) {
  const [carouselIdx, setCarouselIdx] = useState(0)
  const avatar =
    account?.userAvatar || account?.profileImage || account?.avatarUrl || PLACEHOLDER
  const displayName = account?.displayName || 'Pinterest User'

  // Separate images/videos
  const imageFiles = files.filter(f => f.url && !isVideoUrl(f.url))
  const videoFiles = files.filter(f => f.url && isVideoUrl(f.url))

  // If any video files, Pinterest only allows a single video (first)
  if (videoFiles.length > 0) {
    const video = videoFiles[0].url
    return (
      <div
        className="border rounded bg-white mb-3"
        style={{ maxWidth: 250, margin: '0 auto', fontFamily: 'Helvetica, Arial, sans-serif' }}
      >
        {/* header */}
        <div className="d-flex align-items-center p-2">
          <img
            src={avatar}
            alt="avatar"
            className="rounded-circle"
            style={{ width: 24, height: 24, objectFit: 'cover' }}
          />
          <strong className="ms-2" style={{ fontSize: 13 }}>
            {displayName}
          </strong>
        </div>
        {/* video */}
        <video
          src={video}
          controls
          style={{ width: '100%', height: 300, objectFit: 'cover', borderRadius: 6 }}
        />
        {/* caption */}
        <div className="p-2">
          {caption ? (
            <p className="mb-0" style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
              {caption}
            </p>
          ) : (
            <p className="text-muted mb-0" style={{ fontSize: 14 }}>
              Add a description…
            </p>
          )}
          <small className="text-muted">
            {postType === 'schedule' && scheduleDateTime
              ? `Scheduled · ${new Date(scheduleDateTime).toLocaleString()}`
              : 'Just now'}
          </small>
        </div>
      </div>
    )
  }

  // Images: show carousel if > 1, otherwise single image
  const hasCarousel = imageFiles.length > 1
  const mainImage = imageFiles.length > 0 ? imageFiles[carouselIdx]?.url : null

  return (
    <div
      className="border rounded bg-white mb-3"
      style={{ maxWidth: 250, margin: '0 auto', fontFamily: 'Helvetica, Arial, sans-serif' }}
    >
      {/* header */}
      <div className="d-flex align-items-center p-2">
        <img
          src={avatar}
          alt="avatar"
          className="rounded-circle"
          style={{ width: 24, height: 24, objectFit: 'cover' }}
        />
        <strong className="ms-2" style={{ fontSize: 13 }}>
          {displayName}
        </strong>
      </div>

      {/* image carousel or single image */}
      <div style={{ position: 'relative', width: '100%', height: 300 }}>
        {mainImage ? (
          <img
            src={mainImage}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }}
          />
        ) : (
          <div
            className="bg-light d-flex align-items-center justify-content-center"
            style={{ width: '100%', height: 300, color: '#999', borderRadius: 6 }}
          >
            No media
          </div>
        )}
        {/* Carousel controls */}
        {hasCarousel && (
          <>
            {/* Left arrow */}
            <button
              type="button"
              className="btn btn-light"
              disabled={carouselIdx === 0}
              style={{
                position: 'absolute',
                top: '50%',
                left: 4,
                transform: 'translateY(-50%)',
                opacity: carouselIdx === 0 ? 0.4 : 0.85,
                zIndex: 2,
                padding: '0 6px',
                borderRadius: '50%',
                fontWeight: 'bold',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                minWidth: 28,
                minHeight: 28
              }}
              onClick={() => setCarouselIdx(i => Math.max(i - 1, 0))}
            >
              <i className="fas fa-chevron-left" />
            </button>
            {/* Right arrow */}
            <button
              type="button"
              className="btn btn-light"
              disabled={carouselIdx === imageFiles.length - 1}
              style={{
                position: 'absolute',
                top: '50%',
                right: 4,
                transform: 'translateY(-50%)',
                opacity: carouselIdx === imageFiles.length - 1 ? 0.4 : 0.85,
                zIndex: 2,
                padding: '0 6px',
                borderRadius: '50%',
                fontWeight: 'bold',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                minWidth: 28,
                minHeight: 28
              }}
              onClick={() => setCarouselIdx(i => Math.min(i + 1, imageFiles.length - 1))}
            >
              <i className="fas fa-chevron-right" />
            </button>
            {/* Dots */}
            <div style={{
              position: 'absolute',
              bottom: 12,
              width: '100%',
              textAlign: 'center',
              zIndex: 2
            }}>
              {imageFiles.map((_, idx) => (
                <span
                  key={idx}
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    margin: '0 2px',
                    borderRadius: '50%',
                    background: idx === carouselIdx ? '#E60023' : '#fff',
                    border: '1.5px solid #E60023'
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* title / description */}
      <div className="p-2">
        {caption ? (
          <p className="mb-0" style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
            {caption}
          </p>
        ) : (
          <p className="text-muted mb-0" style={{ fontSize: 14 }}>
            Add a description…
          </p>
        )}
        <small className="text-muted">
          {postType === 'schedule' && scheduleDateTime
            ? `Scheduled · ${new Date(scheduleDateTime).toLocaleString()}`
            : 'Just now'}
        </small>
      </div>
    </div>
  )
}
